// src/App.js
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import LoginPage from "scenes/loginPage";
import ProfilePage from "scenes/profilePage";
import Properties from "scenes/GeneralProperties/Properties";
import Tenants from "scenes/Tenants"
import Users from "scenes/Users"
import Tickets from "scenes/Tickets";
import Payments from "scenes/Payments";
import Dashboard from "scenes/Dashboard";
import Landlords from "scenes/GeneralLandlords/Landlords";
import Brokers from "scenes/Brokers";
import Manager from "scenes/Management/Managers/Managers";
import PropertiesManaged from "scenes/Management/PropertiesManaged.jsx/index.jsx";
import ReferredLandlords from "scenes/GeneralLandlords/ReferredLandlords";
import NotFound from "scenes/404/Notfound";
import LandlordExpenses from "scenes/GeneralLandlords/LandlordExpenses";
import Units from "components/Tables/UnitsTable";
import PropertyDetails from "scenes/GeneralProperties/Properties/propertyDetails";
import BrokerDetails from "scenes/Brokers/BrokerDetails"
import TenantDetails from "scenes/Users/TenantDetails";
import { CssBaseline, ThemeProvider, Box, Fab } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { themeSettings } from "./theme";
import TenantReceipt from "scenes/Tenants/Receipt";
import BrokerPayments from "scenes/Brokers/BrokerPayments";
import BrokerCommissions from "scenes/Brokers/BrokerCommissions";
import BrokerCommissionPayouts from "scenes/Brokers/BrokerCommissionPayouts";
import BrokerLandlords from "scenes/Brokers/BrokerLandlords";
import HomeRoute from "components/HomeRoute";
import LandlordTenants from "scenes/GeneralLandlords/Landlords/LandlordTenants";
import Booking from "scenes/Booking";
import BookingDetails from "scenes/Booking/BookingDetails";
import LandlordTenantsPayments from "scenes/GeneralLandlords/Landlords/LandlordTenantsPayments";
import LoanRequests from "scenes/TenantsLoans";
import LoanReview from "scenes/TenantsLoans/LoanReview";
import UnitDetails from "scenes/GeneralProperties/Properties/unitDetails";
import RegisteredProperties from "scenes/Management/Managers/registeredProperties";
import Sidebar from "scenes/global/NewSidebar";
import Navbar from "scenes/navbar";
import Settings from "scenes/Settings";
import ManagementTab from "scenes/Management";
import GeneralPropertiesTab from "scenes/GeneralProperties";
import LandLordDetailsTab from "scenes/GeneralLandlords/Landlords/landlordTab";
import GeneralLandlordsTab from "scenes/GeneralLandlords";
import PropertyDetailsTab from "scenes/GeneralProperties/Properties/propertyTab";
import RegistrationConfirmation from "scenes/GeneralLandlords/RegisteredLandlords/registrationConfirmation";
import OnboardingConfirmation from "scenes/GeneralLandlords/PendingOnboardingLandlords/onboardingConfirmation";
import UnitCreate from "scenes/GeneralProperties/Properties/unitCreate";
import Schedules from "scenes/Schedues";
import SystemLogsTab from "scenes/errorLogs";
import CompletedSchedules from "scenes/CompletedSchedules";
import Reports from "scenes/Reports";
import Organisations from "scenes/Organisations";
import ReallocateLandlords from "scenes/ReallocateLandlords";
import RelationshipManagers from "scenes/RelationshipManagers";
import AnonymousTenancies from "scenes/AnonymousTenancies";
import AnonymousLandlords from "scenes/AnonymousLandlords";
import PaymentCharges from "scenes/PaymentCharges";
import Notifications from "scenes/Notifications";
import AgreementTemplates from "scenes/AgreementTemplates";
import BackgroundReportPrefetch from "scenes/global/BackgroundReportPrefetch";

function App() {
  const mode = useSelector((state) => state.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  const [isSidebar, setIsSidebar] = useState(true);
  const isAuth = Boolean(useSelector((state) => state.token));
  const [isCollapsed, setIsCollapsed] = useState(true);
  const handleIsCollapsed = () => setIsCollapsed(!isCollapsed);

  return (
    <div className="app">
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {isAuth ? (
            <>
              <BackgroundReportPrefetch />
              <Navbar setIsSidebar={setIsSidebar} isCollapsed={isCollapsed} handleIsCollapsed={handleIsCollapsed}/>
              <Box sx={{ display: "flex"}}>
                <Sidebar isSidebar={isSidebar} isCollapsed={isCollapsed} handleIsCollapsed={handleIsCollapsed}/>
                <main className="content">
                  <Box>
                    <Routes>
                      <Route path="/" element={<Navigate to="/home" />} />
                      <Route path="/home" element={<HomeRoute><Dashboard /></HomeRoute>} />
                      <Route path="/organisations" element={<Organisations />} />
                      <Route path="/reallocate-landlords" element={<ReallocateLandlords />} />
                      <Route path="/relationship-managers" element={<RelationshipManagers />} />
                      <Route path="/anonymous-tenancies" element={<AnonymousTenancies />} />
                      <Route path="/anonymous-landlords" element={<AnonymousLandlords />} />
                      <Route path="/payment-charges" element={<PaymentCharges />} />
                      <Route path="/agreement-templates/*" element={<AgreementTemplates />} />
                      <Route path="/notifications" element={<Notifications />} />
                      <Route path="/tenants" element={<Users />} />
                      <Route path="/saving-tenants" element={<Tenants />} />
                      <Route path="/payments" element={<Payments />} />
                      <Route path="/generalProperties" element={<GeneralPropertiesTab />} />
                      <Route path="/properties" element={<Properties />} />
                      <Route path="/propertyDetailsTab" element={<PropertyDetailsTab />} />
                      <Route path="/units" element={<Units />} />
                      <Route path="/details" element={<PropertyDetails />} />
                      <Route path="/broker-details" element={<BrokerDetails />} />
                      <Route path="/payments" element={<Payments />} />
                      <Route path="/landlords" element={<GeneralLandlordsTab />} />
                      <Route path="/landlordDetails" element={<LandLordDetailsTab />} />
                      <Route path="/brokers" element={<Brokers />} />
                      <Route path="/landlord-expenses" element={<LandlordExpenses />} />
                      <Route path="/managers" element={<Manager />} />
                      <Route path="schedules" element={<Schedules/>}/>
                      <Route path="/referred-landlords" element={<ReferredLandlords />} />
                      <Route path="/properties-managed" element={<PropertiesManaged />} />
                      <Route path="/broker-payments" element={<BrokerPayments />} />
                      <Route path="/broker-commissions" element={<BrokerCommissions />} />
                      <Route path="/broker-commission-payouts" element={<BrokerCommissionPayouts />} />
                      <Route path="/broker-landlords" element={<BrokerLandlords />} />
                      <Route path="/Landlord-tenants" element={<LandlordTenants />} />
                      <Route path="/registrationConfirmation" element={<RegistrationConfirmation/>}/>
                      <Route path="/onboardingConfirmation" element={<OnboardingConfirmation/>}/>
                      <Route path="/TenantDetails" element={<TenantDetails />} />
                      <Route path="/tenants-loans" element={<LoanRequests />} />
                      <Route path="/tenant-payments" element={<LandlordTenantsPayments />} />
                      <Route path="/tenant-receipts" element={<TenantReceipt />} />
                      <Route path="/bookings" element={<Booking />} />
                      <Route path="/booking-details" element={<BookingDetails />} />
                      <Route path="/loan-review" element={<LoanReview />} />
                      <Route path="/units/details" element={<UnitDetails />} />
                      <Route path="/unitCreate" element={<UnitCreate />} />
                      <Route path="/registered-properties" element={<RegisteredProperties />} />
                      <Route path="/profile/:userId" element={<ProfilePage />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/managment" element={<ManagementTab />} />
                      <Route path="/systemLogs" element={<SystemLogsTab />} />
                      <Route path="/completed-schedules" element={<CompletedSchedules />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Box>
                </main>
              </Box>
            </>
          ) : (
            <main className="content">
              <Routes>
                <Route path="/" element={<Navigate to="/login" />} />
                <Route path="/login" element={<LoginPage />} />
              </Routes>
            </main>
          )}
          
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
